/**
 * Shared helpers for reading the `images` column on properties and units.
 *
 * Both `properties.images` and `units.images` are stored as a JSON array of
 * public storage URLs (photos and the occasional PDF document). Several views
 * used to each roll their own parser — staff PropertyDetails / UnitDetails /
 * PropertyCardEnhanced and, missing entirely, the owner portal (which showed a
 * placeholder icon even when real images existed). This is the single source
 * of truth so every surface reads images the same way.
 */

/**
 * Parse a property/unit `images` JSON value into a clean list of URL strings.
 * Tolerates null, non-array, and malformed entries — only non-empty strings
 * survive.
 */
export function parseImages(imagesJson: unknown): string[] {
  if (!imagesJson) return [];
  if (Array.isArray(imagesJson)) {
    return imagesJson.filter(
      (url): url is string => typeof url === "string" && url.length > 0,
    );
  }
  return [];
}

/**
 * Every photo of a property for its gallery: the property's own images first,
 * then each unit's in unit order, without duplicates (a unit often reuses the
 * building's facade shot).
 */
export function collectGalleryImages(
  propertyImages: readonly string[],
  units: ReadonlyArray<{ images?: readonly string[] }>,
): string[] {
  return [...new Set([...propertyImages, ...units.flatMap((u) => u.images ?? [])])];
}

/**
 * Resolve the images to display for a property: its own images, falling back
 * to the first unit's images when the property itself has none. Mirrors the
 * staff property card so the owner portal behaves identically.
 */
export function resolvePropertyDisplayImages(
  property:
    | {
        images?: unknown;
        units?: ReadonlyArray<{ images?: unknown } | null | undefined> | null;
      }
    | null
    | undefined,
): string[] {
  if (!property) return [];
  const own = parseImages(property.images);
  if (own.length > 0) return own;
  const firstUnit = property.units?.[0];
  return firstUnit ? parseImages(firstUnit.images) : [];
}
